/**
 * TemplatePreviewModal
 * Shows a SendGrid template HTML preview with optional live contact substitution.
 * All variables except SendGrid system fields (unsubscribe, etc.) are substituted
 * from this platform — no contacts are loaded into SendGrid.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import {
  type FieldMapping,
  type LookupBucket,
  buildPreviewSubstitutions,
  substituteTemplateVariables,
  isSendGridReservedField,
  findMissingDefaultRowBundles,
  resolveLookupMapping,
} from "../../services/templates.service";
import { buildSendTimeMergeData } from "../../utils/flightSendTimeData";
import { loadLookupIndexForPreview } from "../../services/lookups.service";
import {
  getContact,
  searchContacts,
  type ContactPickerResult,
} from "../../services/contacts.service";
import {
  contactPickerCanSearch,
  contactPickerStatus,
} from "../../utils/contactPickerStatus";
import { resolveContentUrlsForMappings } from "../../services/content.service";
import { computeTemplateReadiness } from "../../utils/flightPreview";
import {
  CRM_SYSTEM_LINK_NOTE,
  FLIGHT_PREHEADER_NOTE,
  FLIGHT_SUBJECT_AND_PREHEADER_NOTE,
  FLIGHT_SUBJECT_CONDITIONAL_NOTE,
  FLIGHT_SUBJECT_NOTE,
} from "../../utils/systemTemplateVariables";

const FLIGHT_NOTES = [
  FLIGHT_SUBJECT_NOTE,
  FLIGHT_PREHEADER_NOTE,
  FLIGHT_SUBJECT_AND_PREHEADER_NOTE,
  FLIGHT_SUBJECT_CONDITIONAL_NOTE,
];
import type { Contact } from "../../types/contact.types";

interface TemplatePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  templateHtml: string;
  templateName?: string;
  fieldMappings: FieldMapping[];
  /** Flight has an email category (unsubscribe group); the CRM's unsubscribe/preferences links
   *  are filled in at send time only when it does. Omit when unknown. */
  hasEmailCategory?: boolean;
  /** The flight's subject line; when non-empty the send fills {{subject}} from it. Omit when unknown. */
  flightSubject?: string | null;
  /** The flight's preheader setting; when non-empty the send fills {{preheader}} from it. Omit when unknown. */
  flightPreheader?: string | null;
}

export default function TemplatePreviewModal({
  isOpen,
  onClose,
  templateHtml,
  templateName,
  fieldMappings,
  hasEmailCategory,
  flightSubject,
  flightPreheader,
}: TemplatePreviewModalProps) {
  const [contactQuery, setContactQuery] = useState("");
  const [contactResults, setContactResults] = useState<ContactPickerResult[]>(
    [],
  );
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [searching, setSearching] = useState(false);
  // "empty" / "error" once the LATEST search settles with nothing to show;
  // searchSeq drops out-of-order responses from earlier keystrokes.
  const [searchOutcome, setSearchOutcome] = useState<
    "idle" | "empty" | "error" | "short" | "load_error"
  >("idle");
  const searchSeq = useRef(0);
  const [showSubs, setShowSubs] = useState(false);
  // Readiness details collapsed by default so the warning list doesn't push the
  // preview below the fold — the summary line still shows the count.
  const [showReadinessDetails, setShowReadinessDetails] = useState(false);
  const [contentUrls, setContentUrls] = useState<Record<string, string>>({});
  const [lookupIndex, setLookupIndex] = useState<Map<string, LookupBucket>>(
    new Map(),
  );
  const [lookupPreviewError, setLookupPreviewError] = useState<string | null>(
    null,
  );
  // The lookupSignature the current lookupIndex was actually loaded for. The broken-bundle banner is
  // only computed when this equals the CURRENT signature — so neither the initial empty index (null)
  // nor a stale index during a mapping change can flash a false "missing default" warning before/while
  // the load for the new mappings is in flight. An empty index mid-load is indistinguishable from a
  // deleted lookup, so we must not judge until the matching load has completed.
  const [loadedLookupSignature, setLoadedLookupSignature] = useState<
    string | null
  >(null);

  // Content IDs referenced by the current mapping set. Memo to avoid
  // refetching on every keystroke.
  const contentIds = useMemo(
    () =>
      fieldMappings
        .filter((m) => m.sourceType === "content" && m.sourceField)
        .map((m) => m.sourceField as string),
    [fieldMappings],
  );

  const lookupSignature = useMemo(
    () =>
      fieldMappings
        .filter((m) => m.sourceType === "lookup")
        .map(
          (m) =>
            `${m.lookupId ?? ""}:${m.lookupValueColumn ?? ""}:${m.lookupResolution ?? "keyed"}`,
        )
        .sort()
        .join("|"),
    [fieldMappings],
  );

  // Load content URLs whenever the mapping set changes. This is the
  // R3-required preview-path fix: previously buildPreviewSubstitutions
  // never received content URLs so {{content_var}} rendered as the
  // visible "[content missing]" placeholder even when the mapping was
  // valid.
  useEffect(() => {
    if (!isOpen || contentIds.length === 0) {
      setContentUrls({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await resolveContentUrlsForMappings(contentIds);
        if (!cancelled) {
          setContentUrls(res.resolved);
        }
      } catch {
        if (!cancelled) {
          setContentUrls({});
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, contentIds]);

  // Lookup-backed content must render from real lookup rows in preview. The
  // old preview passed `undefined`, which made correctly configured newsletter
  // bundles appear blank and encouraged operators to paste HTML elsewhere.
  useEffect(() => {
    if (!isOpen) {
      // Callers keep the modal MOUNTED while closed, so on reopen the index below is stale/empty
      // until the reload finishes. Reset the loaded-signature to null (never matches a real
      // signature) so a valid bundle can't show "Sending is blocked" for the whole reopen load.
      setLookupIndex(new Map());
      setLookupPreviewError(null);
      setLoadedLookupSignature(null);
      return;
    }
    if (!lookupSignature) {
      // No lookup mappings: the (empty) index trivially matches the (empty) signature.
      setLookupIndex(new Map());
      setLookupPreviewError(null);
      setLoadedLookupSignature("");
      return;
    }
    let cancelled = false;
    // Invalidate until THIS load completes so a stale/empty index (reopen, or a refetch of the same
    // signature) is never judged as loaded and can't flash a false "missing default".
    setLoadedLookupSignature(null);
    (async () => {
      try {
        const loaded = await loadLookupIndexForPreview(fieldMappings);
        if (!cancelled) {
          setLookupIndex(loaded);
          setLookupPreviewError(null);
          // Mark THIS signature as loaded — only now may the banner judge these mappings.
          setLoadedLookupSignature(lookupSignature);
        }
      } catch (err) {
        if (!cancelled) {
          setLookupIndex(new Map());
          setLookupPreviewError(
            err instanceof Error ? err.message : "Lookup preview failed",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, lookupSignature, fieldMappings]);

  const handleSearch = useCallback(async (q: string) => {
    const seq = ++searchSeq.current;
    setContactQuery(q);
    setSelectedContact(null);
    setSearchOutcome("idle");
    if (!contactPickerCanSearch(q)) {
      setContactResults([]);
      setSearching(false);
      setSearchOutcome(q.trim().length > 0 ? "short" : "idle");
      return;
    }
    setContactResults([]);
    setSearching(true);
    try {
      const results = await searchContacts(q, 8);
      if (seq !== searchSeq.current) {
        return;
      }
      setContactResults(results);
      setSearchOutcome(results.length === 0 ? "empty" : "idle");
    } catch {
      if (seq !== searchSeq.current) {
        return;
      }
      setContactResults([]);
      setSearchOutcome("error");
    } finally {
      if (seq === searchSeq.current) {
        setSearching(false);
      }
    }
  }, []);

  // The picker row carries only the picker columns; the preview substitutes
  // ANY mapped contact field, so load the full record (by primary key, under
  // the caller's own RLS) before using it.
  const handleSelectContact = async (row: ContactPickerResult) => {
    const seq = ++searchSeq.current;
    setSearchOutcome("idle");
    setContactResults([]);
    setContactQuery(`${row.first_name} ${row.last_name} — ${row.email || ""}`);
    setSearching(true);
    try {
      const full = await getContact(row.id);
      if (seq !== searchSeq.current) {
        return;
      }
      setSelectedContact(full);
    } catch {
      if (seq !== searchSeq.current) {
        return;
      }
      setSearchOutcome("load_error");
    } finally {
      if (seq === searchSeq.current) {
        setSearching(false);
      }
    }
  };

  // Build substitutions for the iframe even with NO contact selected, so
  // contact-independent slots (custom / url / content / landing page / system)
  // render immediately instead of showing raw {{variable}}. Per-recipient slots
  // (contact / voucher / lookup) are dropped until a contact is picked, so they
  // stay visible as {{variable}} rather than resolving to blank.
  const subs = useMemo<Record<string, string>>(() => {
    const all = buildPreviewSubstitutions(
      fieldMappings,
      (selectedContact as unknown as Record<string, unknown>) ?? {},
      lookupIndex,
      contentUrls,
    );
    if (selectedContact) {
      return all;
    }
    const perRecipient = new Set(["contact", "voucher"]);
    const out: Record<string, string> = { ...all };
    for (const m of fieldMappings) {
      if (
        perRecipient.has(m.sourceType) ||
        (m.sourceType === "lookup" && m.lookupResolution !== "default_row")
      ) {
        delete out[m.templateField];
      }
    }
    return out;
  }, [fieldMappings, selectedContact, lookupIndex, contentUrls]);

  const previewHtml = substituteTemplateVariables(templateHtml, subs);

  // Static content bundles (default_row) whose starred default row is missing/deleted or whose mapped
  // column was renamed resolve to a blank value. Surface them as a structural error rather than a
  // false blank — send-time and coverage already fail closed, but the ORIGINAL contract requires
  // preview to expose a missing default. Only computed once loading finished (an empty index mid-load
  // is indistinguishable from a deleted lookup) and when no higher-level read error is already shown.
  const missingBundleFields = useMemo<string[]>(() => {
    // Judge only once the index in hand was loaded for the CURRENT mappings (no flash on first render
    // or during a mapping change), and never when a higher-level read error is already surfaced.
    if (lookupPreviewError || loadedLookupSignature !== lookupSignature) {
      return [];
    }
    return findMissingDefaultRowBundles(fieldMappings, lookupIndex);
  }, [
    fieldMappings,
    lookupIndex,
    lookupSignature,
    loadedLookupSignature,
    lookupPreviewError,
  ]);

  const userMappings = fieldMappings.filter(
    (m) => !isSendGridReservedField(m.templateField),
  );

  // Newsletter MVP: one consolidated "template readiness" verdict — precise
  // warnings (HTML in an escaping slot, bare image/link URL, empty mappings) plus
  // neutral notes (per-recipient lookup values), each with a plain-language fix.
  // SEND-TIME data for deciding whether the flight's subject line fills {{subject}} — exactly what the
  // send renderer will hold (utils/flightSendTimeData.ts). Deliberately NOT `subs`: that adds preview-only
  // values (current_date / current_year, voucher and missing-content placeholders) the send never has.
  const sendTime = useMemo(
    () =>
      buildSendTimeMergeData({
        mappings: fieldMappings,
        contact:
          (selectedContact as unknown as Record<string, unknown>) ?? null,
        contentUrls,
        flightPreheader,
        resolveLookup: (m) =>
          resolveLookupMapping(
            m,
            (selectedContact as unknown as Record<string, unknown>) ?? {},
            m.lookupId ? lookupIndex.get(m.lookupId) : undefined,
          ).resolvedValue,
      }),
    [fieldMappings, selectedContact, contentUrls, flightPreheader, lookupIndex],
  );
  const readiness = useMemo(
    () =>
      computeTemplateReadiness(templateHtml, fieldMappings, {
        contentUrls,
        hasEmailCategory,
        flightSubject,
        flightPreheader,
        mergeData: sendTime.vars,
        mergeUnknown: sendTime.unknown,
      }),
    [
      templateHtml,
      fieldMappings,
      contentUrls,
      hasEmailCategory,
      flightSubject,
      flightPreheader,
      sendTime,
    ],
  );
  const warningCount = readiness.items.filter(
    (i) => i.severity === "warning",
  ).length;

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-start justify-center p-4 pt-16">
        <div
          className="fixed inset-0 bg-black bg-opacity-50"
          onClick={onClose}
        />
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-6xl flex flex-col h-[92vh]">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Template Preview
              </h2>
              {templateName && (
                <p className="text-sm text-gray-500 mt-0.5">{templateName}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Contact picker */}
          <div className="px-6 py-3 border-b border-gray-100 bg-gray-50 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Preview with contact data (optional)
                </label>
                <input
                  type="text"
                  value={contactQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search by name, email, phone or membership number..."
                  className="w-full text-sm px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                />
                {contactResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {contactResults.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => handleSelectContact(c)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-primary-50 flex items-center justify-between"
                      >
                        <span className="font-medium text-gray-900">
                          {c.first_name} {c.last_name}
                        </span>
                        <span className="text-gray-500 text-xs">{c.email}</span>
                      </button>
                    ))}
                  </div>
                )}
                {!searching &&
                  !selectedContact &&
                  contactResults.length === 0 &&
                  searchOutcome !== "idle" && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm text-gray-500">
                      {searchOutcome === "load_error"
                        ? "Could not load this contact, please pick it again."
                        : contactPickerStatus(
                            contactQuery,
                            false,
                            searchOutcome === "error",
                          )}
                    </div>
                  )}
                {searching && (
                  <div className="absolute right-3 top-7 text-gray-400 text-xs">
                    Searching…
                  </div>
                )}
              </div>

              {selectedContact && (
                <div className="flex items-center gap-2 mt-5">
                  <span className="inline-flex items-center gap-1.5 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                    Live substitution active
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedContact(null);
                      setContactQuery("");
                    }}
                    className="text-xs text-gray-400 hover:text-gray-600 mt-0.5"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>

            {/* Variable substitution table (collapsible) */}
            {selectedContact && userMappings.length > 0 && (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setShowSubs((v) => !v)}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                >
                  {showSubs ? "▾ Hide" : "▸ Show"} variable substitutions (
                  {userMappings.length})
                </button>
                {showSubs && (
                  <div className="mt-2 overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="text-left px-2 py-1 font-medium text-gray-600">
                            Variable
                          </th>
                          <th className="text-left px-2 py-1 font-medium text-gray-600">
                            Source
                          </th>
                          <th className="text-left px-2 py-1 font-medium text-gray-600">
                            Resolved value
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {userMappings.map((m) => (
                          <tr
                            key={m.templateField}
                            className="border-t border-gray-100"
                          >
                            <td className="px-2 py-1 font-mono text-blue-700">
                              {`{{${m.templateField}}}`}
                            </td>
                            <td className="px-2 py-1 text-gray-500">
                              {m.sourceType === "contact"
                                ? `contact.${m.sourceField}`
                                : m.sourceType === "voucher"
                                  ? `voucher.${m.sourceField}`
                                  : m.sourceType}
                            </td>
                            <td className="px-2 py-1 text-gray-900 font-medium">
                              {subs[m.templateField] || (
                                <span className="text-amber-500 italic">
                                  empty
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Template readiness (newsletter MVP) */}
          {lookupPreviewError && (
            <div className="px-6 py-3 border-b border-red-200 bg-red-50 text-sm text-red-800">
              Lookup content could not be loaded for preview. Do not rely on
              this preview until it is fixed: {lookupPreviewError}
            </div>
          )}
          {!lookupPreviewError && missingBundleFields.length > 0 && (
            <div className="px-6 py-3 border-b border-red-200 bg-red-50 text-sm text-red-800">
              <p className="font-semibold">
                Static content bundle has no usable default row
              </p>
              <p className="mt-1">
                The blank section
                {missingBundleFields.length > 1 ? "s" : ""}{" "}
                <span className="font-medium">
                  {missingBundleFields.map((f) => `{{${f}}}`).join(", ")}
                </span>{" "}
                {missingBundleFields.length > 1 ? "map" : "maps"} to a static
                content bundle whose starred default row is missing, deleted, or
                has a renamed column — this preview shows{" "}
                {missingBundleFields.length > 1 ? "them" : "it"} blank. Mark a
                default row (star) with the intended content before sending.
                Sending is blocked until this is fixed.
              </p>
            </div>
          )}
          <div
            className={`px-6 py-3 border-b flex-shrink-0 ${
              readiness.status === "ready"
                ? "border-green-100 bg-green-50"
                : "border-amber-100 bg-amber-50"
            }`}
          >
            {readiness.status === "ready" ? (
              <div className="flex items-center gap-2 text-sm text-green-800">
                <span aria-hidden>✓</span>
                <span className="font-semibold">Template looks ready</span>
                <span className="text-green-700">
                  — no issues found in the variables you mapped.
                </span>
              </div>
            ) : null}
            {readiness.status === "ready" &&
            readiness.items.some((i) => i.title === CRM_SYSTEM_LINK_NOTE) ? (
              <div className="mt-1 text-xs text-gray-600">
                <span aria-hidden>ℹ️</span> {CRM_SYSTEM_LINK_NOTE}
              </div>
            ) : null}
            {readiness.status === "ready" &&
            readiness.items.some((i) => FLIGHT_NOTES.includes(i.title)) ? (
              <div className="mt-1 text-xs text-gray-600">
                <span aria-hidden>ℹ️</span>{" "}
                {
                  readiness.items.find((i) => FLIGHT_NOTES.includes(i.title))
                    ?.title
                }
              </div>
            ) : null}
            {readiness.status === "ready" ? null : (
              <button
                type="button"
                onClick={() => setShowReadinessDetails((v) => !v)}
                aria-expanded={showReadinessDetails}
                className="flex items-center gap-2 text-sm text-amber-900 w-full text-left"
              >
                <span aria-hidden>⚠️</span>
                <span className="font-semibold">
                  {warningCount}{" "}
                  {warningCount === 1 ? "item needs" : "items need"} attention
                  before sending
                </span>
                <span className="ml-1 text-xs font-medium text-amber-700">
                  {showReadinessDetails ? "▾ Hide details" : "▸ Show details"}
                </span>
              </button>
            )}

            {readiness.items.length > 0 && showReadinessDetails && (
              <ul className="mt-2 space-y-2 max-h-48 overflow-y-auto pr-1">
                {readiness.items.map((item, i) => (
                  <li key={`${item.field}-${i}`} className="text-xs flex gap-2">
                    <span aria-hidden className="mt-0.5">
                      {item.severity === "warning" ? "⚠️" : "ℹ️"}
                    </span>
                    <div>
                      <div
                        className={
                          item.severity === "warning"
                            ? "text-amber-900"
                            : "text-gray-600"
                        }
                      >
                        {item.title}
                      </div>
                      <div className="text-gray-500 mt-0.5">
                        <span className="font-medium">Fix:</span> {item.fix}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Template HTML */}
          <div className="flex-1 overflow-hidden">
            <iframe
              srcDoc={previewHtml}
              title="Email template preview"
              className="w-full h-full border-0"
              sandbox="allow-same-origin"
            />
          </div>

          {/* Footer */}
          <div className="px-6 py-3 border-t border-gray-200 flex-shrink-0 flex items-center justify-between">
            <p className="text-xs text-gray-400">
              SendGrid system variables (unsubscribe, sender info) are injected
              by SendGrid at send time and shown as placeholders here.
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
